import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeeAPI } from '../services/api';
import Modal from './Modal';
import RoleBasedEmployeeForm from './RoleBasedEmployeeForm';
import './EmployeeManagement.css';

const EmployeeManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showNotification('Failed to load employees', 'info');
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);
  const isAdminUser =
    currentUser?.email === 'admin@crm.com' ||
    (typeof currentUser?.role === 'string' && currentUser.role.toLowerCase() === 'admin');

  const canCreate = isAdminUser || hasPermission('employee_create');
  const canEdit = isAdminUser || hasPermission('employee_edit');
  const canDelete = isAdminUser || hasPermission('employee_delete');

  const handleDelete = async (id) => {
    if (!canDelete) {
      alert('You do not have permission to delete employees');
      return;
    }
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeAPI.delete(id);
        showNotification('Employee deleted successfully', 'success');
        fetchEmployees();
      } catch (error) {
        alert('Error deleting employee: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleCreateEmployee = async (formData) => {
    try {
      const employeeId = `EMP${Date.now().toString().slice(-6)}`;
      const payload = {
        employeeId,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: 'employee',
        customRole: formData.role,
        department: formData.department,
        designation: formData.designation,
        salary: Number(formData.salary),
        joiningDate: formData.dateOfJoining,
        status: 'active',
      };

      await employeeAPI.create(payload);
      setShowModal(false);
      showNotification('Employee created successfully!', 'success');
      fetchEmployees();
    } catch (error) {
      alert('Error creating employee: ' + (error.message || 'Unknown error'));
    }
  };

  const handleViewEmployee = (employee) => {
    setViewEmployee(employee);
  };

  const handleEditStart = (employee) => {
    setEditEmployee(employee);
    setEditForm({
      name: employee.name || '',
      phone: employee.phone || '',
      department: employee.department || '',
      designation: employee.designation || '',
      salary: employee.salary || '',
      status: employee.status || 'active',
      dateOfBirth: employee.dateOfBirth ? employee.dateOfBirth.split('T')[0] : '',
      addressStreet: employee.address?.street || '',
      addressCity: employee.address?.city || '',
      addressState: employee.address?.state || '',
      addressZip: employee.address?.zipCode || '',
      addressCountry: employee.address?.country || '',
      ecName: employee.emergencyContact?.name || '',
      ecRelationship: employee.emergencyContact?.relationship || '',
      ecPhone: employee.emergencyContact?.phone || '',
      skills: Array.isArray(employee.skills) ? employee.skills.join(', ') : (employee.skills || ''),
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async () => {
    if (!editEmployee) return;
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        phone: editForm.phone,
        department: editForm.department,
        designation: editForm.designation,
        salary: Number(editForm.salary),
        status: editForm.status,
        dateOfBirth: editForm.dateOfBirth || undefined,
        address: {
          street: editForm.addressStreet,
          city: editForm.addressCity,
          state: editForm.addressState,
          zipCode: editForm.addressZip,
          country: editForm.addressCountry,
        },
        emergencyContact: {
          name: editForm.ecName,
          relationship: editForm.ecRelationship,
          phone: editForm.ecPhone,
        },
        skills: editForm.skills ? editForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await employeeAPI.update(editEmployee._id || editEmployee.id, payload);
      setEditEmployee(null);
      showNotification('Employee updated successfully!', 'success');
      fetchEmployees();
    } catch (error) {
      alert('Error updating employee: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setEditLoading(false);
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'info-circle'}"></i>
      <span>${message}</span>
    `;
    notification.style.cssText = `
      position: fixed;
      top: 90px;
      right: 30px;
      background: ${type === 'success' ? '#10b981' : '#3b82f6'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 10px;
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const getRoleBadgeClass = (roleName) => {
    if (!roleName) return 'badge-info';
    const normalized = String(roleName).toLowerCase();
    if (normalized.includes('admin')) return 'badge-danger';
    if (normalized.includes('manager') || normalized.includes('lead')) return 'badge-warning';
    return 'badge-info';
  };

  return (
    <div className="employee-management">
      <div className="module-header">
        <h2>Employee Management</h2>
        <div className="header-actions">
          <div className="stats-mini">
            <span><i className="fas fa-users"></i> {employees.length} Total</span>
            <span><i className="fas fa-user-check"></i> {employees.filter(e => e.status === 'active').length} Active</span>
          </div>
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-user-plus"></i> Add Employee
            </button>
          )}
        </div>
      </div>

      {!canCreate && (
        <div className="permission-notice">
          <i className="fas fa-info-circle"></i>
          <span>You have view-only access. Contact your administrator to create or modify employees.</span>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => {
              const roleLabel = emp.customRole?.name || emp.role || 'Employee';
              return (
                <tr key={emp._id || emp.id}>
                  <td><strong>{emp.employeeId || emp.id || '-'}</strong></td>
                  <td>{emp.name}</td>
                  <td>
                    <span className={getRoleBadgeClass(roleLabel)}>
                      {roleLabel}
                    </span>
                  </td>
                  <td>{emp.department}</td>
                  <td>{emp.designation}</td>
                  <td>{emp.email}</td>
                  <td>{emp.phone}</td>
                  <td>
                    <span className={emp.status === 'active' ? 'badge-success' : 'badge-danger'}>
                      {emp.status}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn-icon view" 
                      onClick={() => handleViewEmployee(emp)} 
                      title="View"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    {canEdit && (
                      <button className="btn-icon edit" title="Edit" onClick={() => handleEditStart(emp)}>
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                    {canDelete && (
                      <button 
                        className="btn-icon delete" 
                        onClick={() => handleDelete(emp._id || emp.id)} 
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Employee Modal */}
      <Modal 
        show={showModal} 
        onClose={() => setShowModal(false)} 
        title="Create New Employee"
      >
        <RoleBasedEmployeeForm
          onSubmit={handleCreateEmployee}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      {/* Edit Employee Modal */}
      <Modal
        show={editEmployee !== null}
        onClose={() => setEditEmployee(null)}
        title={`Edit Employee — ${editEmployee?.name || ''}`}
      >
        {editEmployee && (
          <div className="edit-employee-form">
            <div className="edit-section">
              <h4><i className="fas fa-user"></i> Personal Details</h4>
              <div className="edit-grid">
                <div className="edit-field">
                  <label>Full Name *</label>
                  <input name="name" value={editForm.name} onChange={handleEditChange} placeholder="Full Name" />
                </div>
                <div className="edit-field">
                  <label>Phone</label>
                  <input name="phone" value={editForm.phone} onChange={handleEditChange} placeholder="Phone" />
                </div>
                <div className="edit-field">
                  <label>Date of Birth</label>
                  <input type="date" name="dateOfBirth" value={editForm.dateOfBirth} onChange={handleEditChange} />
                </div>
                <div className="edit-field">
                  <label>Status</label>
                  <select name="status" value={editForm.status} onChange={handleEditChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on_leave">On Leave</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="edit-section">
              <h4><i className="fas fa-briefcase"></i> Professional Details</h4>
              <div className="edit-grid">
                <div className="edit-field">
                  <label>Department</label>
                  <input name="department" value={editForm.department} onChange={handleEditChange} placeholder="Department" />
                </div>
                <div className="edit-field">
                  <label>Designation</label>
                  <input name="designation" value={editForm.designation} onChange={handleEditChange} placeholder="Designation" />
                </div>
                <div className="edit-field">
                  <label>Salary (₹)</label>
                  <input type="number" name="salary" value={editForm.salary} onChange={handleEditChange} placeholder="Salary" />
                </div>
                <div className="edit-field">
                  <label>Skills (comma separated)</label>
                  <input name="skills" value={editForm.skills} onChange={handleEditChange} placeholder="e.g. Sales, Excel, CRM" />
                </div>
              </div>
            </div>

            <div className="edit-section">
              <h4><i className="fas fa-map-marker-alt"></i> Address</h4>
              <div className="edit-grid">
                <div className="edit-field edit-field-full">
                  <label>Street</label>
                  <input name="addressStreet" value={editForm.addressStreet} onChange={handleEditChange} placeholder="Street Address" />
                </div>
                <div className="edit-field">
                  <label>City</label>
                  <input name="addressCity" value={editForm.addressCity} onChange={handleEditChange} placeholder="City" />
                </div>
                <div className="edit-field">
                  <label>State</label>
                  <input name="addressState" value={editForm.addressState} onChange={handleEditChange} placeholder="State" />
                </div>
                <div className="edit-field">
                  <label>ZIP Code</label>
                  <input name="addressZip" value={editForm.addressZip} onChange={handleEditChange} placeholder="ZIP Code" />
                </div>
                <div className="edit-field">
                  <label>Country</label>
                  <input name="addressCountry" value={editForm.addressCountry} onChange={handleEditChange} placeholder="Country" />
                </div>
              </div>
            </div>

            <div className="edit-section">
              <h4><i className="fas fa-phone-alt"></i> Emergency Contact</h4>
              <div className="edit-grid">
                <div className="edit-field">
                  <label>Contact Name</label>
                  <input name="ecName" value={editForm.ecName} onChange={handleEditChange} placeholder="Contact Name" />
                </div>
                <div className="edit-field">
                  <label>Relationship</label>
                  <input name="ecRelationship" value={editForm.ecRelationship} onChange={handleEditChange} placeholder="Relationship" />
                </div>
                <div className="edit-field">
                  <label>Contact Phone</label>
                  <input name="ecPhone" value={editForm.ecPhone} onChange={handleEditChange} placeholder="Phone Number" />
                </div>
              </div>
            </div>

            <div className="edit-actions">
              <button className="btn btn-secondary" onClick={() => setEditEmployee(null)} disabled={editLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={editLoading}>
                {editLoading ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Changes</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* View Employee Modal */}
      <Modal 
        show={viewEmployee !== null} 
        onClose={() => setViewEmployee(null)} 
        title="Employee Details"
      >
        {viewEmployee && (
          <div className="employee-details">
            <div className="detail-section">
              <h4><i className="fas fa-user"></i> Personal Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Employee ID:</label>
                  <span>{viewEmployee.employeeId || viewEmployee.id || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Full Name:</label>
                  <span>{viewEmployee.name}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{viewEmployee.email}</span>
                </div>
                <div className="detail-item">
                  <label>Phone:</label>
                  <span>{viewEmployee.phone}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4><i className="fas fa-briefcase"></i> Professional Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Role:</label>
                  <span className={getRoleBadgeClass(viewEmployee.customRole?.name || viewEmployee.role)}>
                    {viewEmployee.customRole?.name || viewEmployee.role || 'Employee'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Department:</label>
                  <span>{viewEmployee.department}</span>
                </div>
                <div className="detail-item">
                  <label>Designation:</label>
                  <span>{viewEmployee.designation}</span>
                </div>
                <div className="detail-item">
                  <label>Salary:</label>
                  <span>${viewEmployee.salary?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={viewEmployee.status === 'active' ? 'badge-success' : 'badge-danger'}>
                    {viewEmployee.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmployeeManagement;
